/**
 * Pure helpers behind the copyable error details (WSRADM-0047).
 *
 * `describeError` flattens whatever a screen caught into one `ErrorReport`;
 * `formatErrorReport` turns it into the plain-text block a tester pastes to
 * support. Nothing here reads the DOM, storage, or a token — `ApiError.details`
 * is built by `client.ts` from the response body and the request's method + path
 * only. Never JSON, never HTML, never a stack trace.
 */
import { ApiError } from '@/api/errors';
import { isObject } from '@/api/parse';
import { BootstrapError } from '@/config/bootstrap';

export interface ErrorReport {
  /** `null` for a network failure or a non-ApiError. */
  status: number | null;
  /** `details.code` (server envelope) ?? `ApiError.code`. */
  code: string | null;
  /** `details.message`, verbatim. */
  message: string | null;
  requestId: string | null;
  method: string | null;
  path: string | null;
  /**
   * `details.server.diagnostic` (WSRAPI-0155, staging only) — string-valued fields
   * only; `at` is `null` on the wire when no application frame was found.
   */
  diagnostic: { exception?: string; message?: string; at?: string } | null;
}

const EMPTY: ErrorReport = {
  status: null,
  code: null,
  message: null,
  requestId: null,
  method: null,
  path: null,
  diagnostic: null,
};

/** A non-empty string kept verbatim (no trimming), else `null`. */
function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function pickDiagnostic(details: Record<string, unknown>): ErrorReport['diagnostic'] {
  const server = details['server'];
  if (!isObject(server)) return null;
  const raw = server['diagnostic'];
  if (!isObject(raw)) return null;
  const out: { exception?: string; message?: string; at?: string } = {};
  const exception = str(raw['exception']);
  const message = str(raw['message']);
  const at = str(raw['at']);
  if (exception !== null) out.exception = exception;
  if (message !== null) out.message = message;
  if (at !== null) out.at = at;
  return Object.keys(out).length > 0 ? out : null;
}

export function describeError(err: unknown): ErrorReport {
  if (err instanceof ApiError) {
    const d = err.details ?? {};
    return {
      status: err.status === 0 ? null : err.status,
      code: str(d['code']) ?? err.code,
      message: str(d['message']),
      requestId: err.requestId ?? null,
      method: str(d['method']),
      path: str(d['path']),
      diagnostic: pickDiagnostic(d),
    };
  }
  if (err instanceof BootstrapError) {
    return {
      ...EMPTY,
      status: err.status === 0 ? null : err.status,
      message: err.message.length > 0 ? err.message : null,
    };
  }
  if (err instanceof Error) {
    return { ...EMPTY, message: err.message.length > 0 ? err.message : null };
  }
  return { ...EMPTY };
}

export interface ErrorReportContext {
  headline: string;
  pageUrl: string;
  at: Date;
  /** Field labels in the current UI locale, keyed by `errors.field.*` suffix. */
  labels: Record<string, string>;
}

/**
 * Plain text, one `Label: value` per line, null lines omitted. Order: headline,
 * time (ISO, UTC), page URL, request (`METHOD path`), status, code, message,
 * request id, diagnostic exception / message / at.
 */
export function formatErrorReport(r: ErrorReport, ctx: ErrorReportContext): string {
  const label = (key: string): string => ctx.labels[key] ?? key;
  const lines: string[] = [ctx.headline];
  const add = (key: string, value: string | number | null | undefined): void => {
    if (value === null || value === undefined || value === '') return;
    lines.push(`${label(key)}: ${value}`);
  };
  add('time', ctx.at.toISOString());
  add('page', ctx.pageUrl);
  const request = [r.method, r.path].filter((v): v is string => v !== null).join(' ');
  add('request', request);
  add('status', r.status);
  add('code', r.code);
  add('message', r.message);
  add('requestId', r.requestId);
  add('exception', r.diagnostic?.exception);
  add('message', r.diagnostic?.message);
  add('at', r.diagnostic?.at);
  return lines.join('\n');
}
